import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { LoaderCircle, Send, X, Plus, Mail, Info } from 'lucide-react'

import * as Mutation from '../../../api/gql/mutation'

interface Props {
  isOpen: boolean
  onClose: () => void
  meetingId: string
  baselineEmails: string[]
  // Pre-formatted date of a previous distribution, if any (null = never sent).
  alreadySentAt?: string | null
  /** Confidential meeting: the recipient list is the participants, full stop —
   *  the extra-emails input is not rendered and the server refuses it anyway. */
  confidential?: boolean
}

const DistributeMeetingDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  meetingId,
  baselineEmails,
  alreadySentAt,
  confidential = false,
}) => {
  const { t } = useTranslation()
  const [extraInput, setExtraInput] = useState('')
  const [extraEmails, setExtraEmails] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  const [distributeMeeting] = useMutation(Mutation.DISTRIBUTE_MEETING)

  // The definitive "who will receive this" list: baseline recipients (organizer +
  // attendees + external guests, computed server-side) plus any manually added
  // extras. Baseline entries are intrinsic and cannot be removed here.
  const recipients = [
    ...baselineEmails.map((email) => ({ email, removable: false })),
    ...extraEmails.map((email) => ({ email, removable: true })),
  ]

  const handleAdd = () => {
    const val = extraInput.trim()
    if (!val) return
    if (!val.includes('@')) {
      toastr.error(t('Common_error'), t('Meeting_distribute_invalid_email'))
      return
    }
    const lower = val.toLowerCase()
    const duplicate = [...baselineEmails, ...extraEmails].some(
      (e) => e.toLowerCase() === lower
    )
    if (duplicate) {
      setExtraInput('')
      return
    }
    setExtraEmails([...extraEmails, val])
    setExtraInput('')
  }

  const handleSend = async () => {
    setSubmitting(true)
    try {
      await distributeMeeting({
        variables: { id: meetingId, extraEmails },
      })
      toastr.success(t('Common_success'), t('Meeting_distribute_success'))
      onClose()
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Meeting_distribute_title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {alreadySentAt && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{t('Meeting_distribute_already_sent', { date: alreadySentAt })}</span>
            </div>
          )}

          <div>
            <div className="mb-1 flex items-center justify-between">
              <Label>{t('Meeting_distribute_recipients_label')}</Label>
              <Badge variant="secondary">{recipients.length}</Badge>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">
              {t('Meeting_distribute_recipients_hint')}
            </p>
            {recipients.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('Meeting_distribute_baseline_empty')}
              </p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {recipients.map(({ email, removable }) => (
                  <Badge key={email} variant="outline" className="gap-1">
                    <Mail className="h-3 w-3 opacity-60" />
                    {email}
                    {removable && (
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() =>
                          setExtraEmails(extraEmails.filter((x) => x !== email))
                        }
                      />
                    )}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {!confidential && (
          <div>
            <Label className="block mb-2">{t('Meeting_distribute_extra_label')}</Label>
            <div className="flex gap-2">
              <Input
                placeholder="name@example.com"
                value={extraInput}
                onChange={(e) => setExtraInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAdd()
                  }
                }}
              />
              <Button variant="outline" size="sm" onClick={handleAdd}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('Common_cancel')}
          </Button>
          <Button onClick={handleSend} disabled={submitting}>
            {submitting ? (
              <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {t('Meeting_distribute_send')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default DistributeMeetingDialog
